import { Button } from '@components/ui/button';
import { ResponsiveDialog } from '@components/ui/dialog/responsive';
import { useIsPhone } from '@hooks/useIsPhone';
import { Form, FormField, FormMessage } from '@components/ui/form';
import { Input } from '@components/ui/form/input';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { useToast } from '@hooks/useToast';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileInput } from '@hooks/useFileInput';
import { FileInput } from '@components/ui/form/file';
import { cn } from '@utils/cn';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useDropdownsContext } from '@contexts/dropdowns';
import { FormRequired } from '@components/ui/form/required';
import { UploadFileDialogProps } from './types';
import { SelectInput } from '@components/ui/form/select/input';
import { useFileForm } from '@forms/files';
import { usePostHog } from '@posthog/react';

const maxLength = 100;

export const UploadFileDialog = ({
  jobId,
  open,
  setOpen,
  initialValues,
  initialSubtaskId,
  title,
  dragAndDrop,
  queryType = 'documents',
  withCameraAccess = false,
}: UploadFileDialogProps) => {
  const {
    subtasksForFiles: { options, disabled },
    setParams,
  } = useDropdownsContext();

  const { toast } = useToast();
  const { t } = useTranslation('app');
  const posthog = usePostHog();
  const isPhone = useIsPhone();
  const buttonSize = isPhone ? 'lg' : 'default';

  const fileInputProps = useFileInput();
  const { files, setFiles } = fileInputProps;

  const { form, onSubmit } = useFileForm({
    id: initialValues?.id,
    jobId,
    files,
    queryType,
    successHandler: () => {
      posthog?.capture('document_uploaded', {
        job_id: jobId,
        is_update: Boolean(initialValues),
        query_type: queryType,
      });
      toast({
        title: t(`messages.success.file.${initialValues ? 'update' : 'upload'}`),
      });
    },
  });

  const { setValue, reset } = form;
  const subtaskId = form.watch('subtaskId');

  useEffect(() => {
    if (!open) setFiles(null);
    if (!initialValues) reset();
    else {
      setValue('subtaskId', initialValues.subtaskId || 'other');
      if (!initialValues.subtaskId) setValue('name', initialValues?.name || '');
    }
    if (initialSubtaskId) setValue('subtaskId', initialSubtaskId);
  }, [initialValues, open, setValue, reset, setFiles, initialSubtaskId]);

  useEffect(() => {
    if (!jobId) return;
    if (!open) setParams({ subtasksForFiles: {} });
    else setParams({ subtasksForFiles: { jobId } });
  }, [open, jobId, setParams]);

  return (
    <ResponsiveDialog open={open} setOpen={setOpen} title={title || t('fileUpload.title')}>
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit((values) => {
            onSubmit(values);
            form.reset();
            setFiles(null);
            setOpen(false);
          })}
          className="w-full space-y-4">
          <div
            className={cn([
              'flex gap-4 [&>*]:w-full',
              !initialSubtaskId && !isPhone && '[&>*]:max-w-[223px]',
              isPhone && 'flex-col',
            ])}>
            {!initialSubtaskId && (
              <FormField
                control={form.control}
                name="subtaskId"
                render={({ field: { value, onChange } }) => (
                  <FormItemWrapper label={t('forms.documentType.label')} select required>
                    <SelectInput
                      name="subtaskId"
                      value={value}
                      onChange={onChange}
                      options={options
                        .concat([{ id: 'other', name: t('pages.job.documents.other') }])
                        .map((el) => ({ name: el.name, value: el.id }))}
                      placeholder={t('forms.default.select.placeholder')}
                      disabled={disabled || Boolean(initialValues)}
                    />
                  </FormItemWrapper>
                )}
              />
            )}
            {subtaskId === 'other' && (
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItemWrapper label={t('forms.documentName.label')} required>
                    <Input {...field} />
                  </FormItemWrapper>
                )}
              />
            )}
          </div>
          <FileInput
            {...fileInputProps}
            maxLength={maxLength}
            accept="*"
            multiple={!initialValues}
            dragAndDrop={dragAndDrop && !isPhone}
            allowFolders={!isPhone && !initialValues}
            withCameraAccess={withCameraAccess}
          />
          <div className="flex items-center justify-between [&>*]:w-fit">
            <FormRequired>{t('fileUpload.maxLength', { maxLength })}</FormRequired>
            <FormRequired />
          </div>
          <FormField name="root" render={() => <FormMessage />} />
          <div
            className={cn(
              'flex items-center justify-end gap-2',
              isPhone && 'flex-col-reverse items-stretch',
            )}>
            <Button
              variant="secondary"
              type="button"
              size={buttonSize}
              onClick={() => setOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              type="submit"
              size={buttonSize}
              disabled={Boolean(!files || !Array.from(files).length)}>
              {initialSubtaskId && (
                <span>
                  <SvgConsumer className="text-m" id="jobCompleted" />
                </span>
              )}
              {t(`actions.${initialSubtaskId ? 'confirm' : 'save'}`)}
            </Button>
          </div>
        </form>
      </Form>
    </ResponsiveDialog>
  );
};
